/**
 * Hierarchy Navigation Service for SPLH Dashboard
 *
 * This service provides FRONTEND-ONLY navigation helpers for managing
 * breadcrumb state and hierarchy drilldown UI. It does NOT perform any
 * data aggregation, filtering, or calculations - all data comes pre-calculated
 * from the backend.
 *
 * HIERARCHY MODEL:
 * - Location is the FOUNDATION - every client has Locations
 * - OrgLevels are OPTIONAL organizational groupings above Locations
 * - OrgLevels build UP from Location:
 *   - ORGLEVEL_1 (order: 1) = directly above Location (e.g., "Area")
 *   - ORGLEVEL_2 (order: 2) = above ORGLEVEL_1 (e.g., "District")
 *   - ORGLEVEL_3 (order: 3) = above ORGLEVEL_2 (e.g., "Region")
 * - A client must have lower OrgLevels to have higher ones
 *   (cannot have ORGLEVEL_2 without ORGLEVEL_1)
 * - Each Location has embedded org structure (orgLevel_1, orgLevel_2, orgLevel_3 fields)
 *
 * DRILLDOWN DIRECTION (top-to-bottom in UI):
 * - Highest OrgLevel (if any) → lower OrgLevels → Location
 * - Example with 2 OrgLevels: ORGLEVEL_2 → ORGLEVEL_1 → LOCATION_LEVEL
 * - Example with no OrgLevels: Just Locations (flat list)
 *
 * Key Principle: When a user drills down or changes hierarchy scope,
 * the frontend triggers a new backend query. The backend re-aggregates
 * all data for the new scope.
 *
 * @module pages/common/dashboards/SPLHDashboard/services/hierarchyService
 */

import { ORG_LEVELS } from '../constants';

/**
 * Default level labels (used when hierarchyMetadata not available)
 * @constant {Object}
 */
const DEFAULT_LEVEL_LABELS = {
  [ORG_LEVELS.ORGLEVEL_1]: 'Level 1',
  [ORG_LEVELS.ORGLEVEL_2]: 'Level 2',
  [ORG_LEVELS.ORGLEVEL_3]: 'Level 3',
  [ORG_LEVELS.LOCATION_LEVEL]: 'Location',
};

/**
 * Breadcrumb item structure
 * @typedef {Object} BreadcrumbItem
 * @property {string} id - Node ID
 * @property {string} name - Display name
 * @property {string} level - OrgLevel enum value
 * @property {string} levelLabel - Human-readable level label from hierarchyMetadata
 */

/**
 * Drilldown path item structure
 * @typedef {Object} DrilldownPathItem
 * @property {string} id - Node ID
 * @property {string} name - Display name
 * @property {string} level - OrgLevel enum value
 */

/**
 * Builds a breadcrumb trail from the current drilldown path
 *
 * @param {DrilldownPathItem[]} drilldownPath - Array of drilldown selections (highest level first)
 * @param {Object} [hierarchyMetadata] - Backend metadata with level display names
 * @returns {BreadcrumbItem[]} Array of breadcrumb items with level labels
 *
 * @example
 * // Client with 2 OrgLevels: Region (ORGLEVEL_2) → Area (ORGLEVEL_1) → Store
 * const path = [
 *   { id: 'region-east', name: 'East Region', level: 'ORGLEVEL_2' },
 *   { id: 'area-nyc', name: 'NYC Area', level: 'ORGLEVEL_1' },
 * ];
 * const metadata = {
 *   levels: [
 *     { level: 'ORGLEVEL_1', displayName: 'Area', order: 1, isUsed: true },
 *     { level: 'ORGLEVEL_2', displayName: 'Region', order: 2, isUsed: true },
 *   ]
 * };
 * buildBreadcrumb(path, metadata);
 * // Returns: [
 * //   { id: 'region-east', name: 'East Region', level: 'ORGLEVEL_2', levelLabel: 'Region' },
 * //   { id: 'area-nyc', name: 'NYC Area', level: 'ORGLEVEL_1', levelLabel: 'Area' },
 * // ]
 */
export function buildBreadcrumb(drilldownPath = [], hierarchyMetadata = null) {
  if (!drilldownPath || !Array.isArray(drilldownPath)) {
    return [];
  }

  return drilldownPath.map((item) => ({
    ...item,
    levelLabel: getHierarchyLevelLabel(item.level, hierarchyMetadata),
  }));
}

/**
 * Gets the display label for a hierarchy level from backend metadata
 *
 * @param {string} level - OrgLevel enum value (e.g., 'ORGLEVEL_1', 'LOCATION_LEVEL')
 * @param {Object} [hierarchyMetadata] - Backend metadata with level display names
 * @returns {string} Human-readable level label
 *
 * @example
 * const metadata = {
 *   levels: [
 *     { level: 'ORGLEVEL_1', displayName: 'Area', order: 1, isUsed: true },
 *     { level: 'ORGLEVEL_2', displayName: 'Region', order: 2, isUsed: true },
 *   ]
 * };
 * getHierarchyLevelLabel('ORGLEVEL_1', metadata);
 * // Returns: 'Area'
 *
 * @example
 * // Without metadata, falls back to defaults
 * getHierarchyLevelLabel('LOCATION_LEVEL', null);
 * // Returns: 'Location'
 */
export function getHierarchyLevelLabel(level, hierarchyMetadata = null) {
  if (!level) {
    return '';
  }

  // Try to get label from backend metadata
  if (hierarchyMetadata?.levels) {
    const levelInfo = hierarchyMetadata.levels.find((l) => l.level === level);
    if (levelInfo?.displayName) {
      return levelInfo.displayName;
    }
  }

  // Fall back to defaults
  return DEFAULT_LEVEL_LABELS[level] || level;
}

/**
 * Gets all active levels from hierarchy metadata, sorted for drilldown navigation
 * (highest OrgLevel first, Location last)
 *
 * Hierarchy model reminder:
 * - Location is always present (the foundation)
 * - OrgLevels are optional and build UP from Location
 * - order: 1 = ORGLEVEL_1 (directly above Location)
 * - order: 2 = ORGLEVEL_2 (above ORGLEVEL_1)
 * - order: 3 = ORGLEVEL_3 (above ORGLEVEL_2)
 *
 * For drilldown UI, we sort DESCENDING by order so the highest level appears first.
 *
 * @param {Object} [hierarchyMetadata] - Backend metadata with level configuration
 * @returns {Array<{ level: string, displayName: string, order: number }>} Active levels sorted for drilldown (highest first)
 *
 * @example
 * // Client with 2 OrgLevels
 * const metadata = {
 *   levels: [
 *     { level: 'LOCATION_LEVEL', displayName: 'Store', order: 0, isUsed: true },
 *     { level: 'ORGLEVEL_1', displayName: 'Area', order: 1, isUsed: true },
 *     { level: 'ORGLEVEL_2', displayName: 'Region', order: 2, isUsed: true },
 *     { level: 'ORGLEVEL_3', displayName: 'Division', order: 3, isUsed: false },
 *   ]
 * };
 * getActiveLevels(metadata);
 * // Returns (sorted for drilldown - highest first):
 * // [
 * //   { level: 'ORGLEVEL_2', displayName: 'Region', order: 2, isUsed: true },
 * //   { level: 'ORGLEVEL_1', displayName: 'Area', order: 1, isUsed: true },
 * //   { level: 'LOCATION_LEVEL', displayName: 'Store', order: 0, isUsed: true },
 * // ]
 *
 * @example
 * // Client with no OrgLevels (just Locations)
 * const metadata = {
 *   levels: [
 *     { level: 'LOCATION_LEVEL', displayName: 'Store', order: 0, isUsed: true },
 *   ]
 * };
 * getActiveLevels(metadata);
 * // Returns: [{ level: 'LOCATION_LEVEL', displayName: 'Store', order: 0, isUsed: true }]
 */
export function getActiveLevels(hierarchyMetadata = null) {
  if (!hierarchyMetadata?.levels) {
    // Return just Location when no metadata (safest default)
    return [
      {
        level: ORG_LEVELS.LOCATION_LEVEL,
        displayName: DEFAULT_LEVEL_LABELS[ORG_LEVELS.LOCATION_LEVEL],
        order: 0,
      },
    ];
  }

  // Filter to active levels and sort DESCENDING by order (highest OrgLevel first for drilldown)
  return hierarchyMetadata.levels
    .filter((l) => l.isUsed !== false) // Include if isUsed is true or undefined
    .sort((a, b) => (b.order ?? 0) - (a.order ?? 0)); // Descending order
}

/**
 * Gets the highest (root) level for this client's hierarchy
 * This is where drilldown navigation starts
 *
 * @param {Object} [hierarchyMetadata] - Backend metadata with level configuration
 * @returns {{ level: string, displayName: string, order: number } | null} Highest active level or null
 *
 * @example
 * // Client with Region → Area → Location
 * getHighestLevel(metadata);
 * // Returns: { level: 'ORGLEVEL_2', displayName: 'Region', order: 2, isUsed: true }
 */
export function getHighestLevel(hierarchyMetadata = null) {
  const activeLevels = getActiveLevels(hierarchyMetadata);
  return activeLevels.length > 0 ? activeLevels[0] : null;
}

/**
 * Checks if the client has any OrgLevels (organizational groupings above Location)
 *
 * @param {Object} [hierarchyMetadata] - Backend metadata with level configuration
 * @returns {boolean} True if client has at least one OrgLevel
 */
export function hasOrgLevels(hierarchyMetadata = null) {
  const activeLevels = getActiveLevels(hierarchyMetadata);
  return activeLevels.some((l) => l.level !== ORG_LEVELS.LOCATION_LEVEL);
}

/**
 * Determines the navigation result for drilling into a node
 *
 * @param {DrilldownPathItem[]} currentPath - Current drilldown path
 * @param {Object} node - The node being drilled into
 * @param {string} node.id - Node ID
 * @param {string} node.name - Node name
 * @param {string} node.level - Node's OrgLevel
 * @param {boolean} node.hasChildren - Whether node has child nodes (more levels to drill into)
 * @returns {{ path: DrilldownPathItem[], canDrilldown: boolean }} New path and drilldown capability
 *
 * @example
 * // Drilling from Region to Area
 * const currentPath = [{ id: 'region-east', name: 'East', level: 'ORGLEVEL_2' }];
 * const node = { id: 'area-nyc', name: 'NYC Area', level: 'ORGLEVEL_1', hasChildren: true };
 * navigateToDrilldown(currentPath, node);
 * // Returns: {
 * //   path: [
 * //     { id: 'region-east', name: 'East', level: 'ORGLEVEL_2' },
 * //     { id: 'area-nyc', name: 'NYC Area', level: 'ORGLEVEL_1' }
 * //   ],
 * //   canDrilldown: true  // Can still drill down to Locations
 * // }
 */
export function navigateToDrilldown(currentPath = [], node) {
  if (!node) {
    return { path: currentPath, canDrilldown: false };
  }

  const newPathItem = {
    id: node.id,
    name: node.name,
    level: node.level,
  };

  const newPath = [...currentPath, newPathItem];

  return {
    path: newPath,
    canDrilldown: Boolean(node.hasChildren),
  };
}

/**
 * Navigates up to a specific breadcrumb level
 *
 * @param {DrilldownPathItem[]} currentPath - Current drilldown path
 * @param {number} targetIndex - Index of the breadcrumb to navigate to (0 = root/highest level)
 * @returns {DrilldownPathItem[]} New path truncated to target index (inclusive)
 *
 * @example
 * // Path: Region → Area → Store, navigate back to Region
 * const path = [
 *   { id: 'region', name: 'East', level: 'ORGLEVEL_2' },
 *   { id: 'area', name: 'NYC', level: 'ORGLEVEL_1' },
 *   { id: 'store', name: 'Store #123', level: 'LOCATION_LEVEL' },
 * ];
 * navigateToLevel(path, 0);
 * // Returns: [{ id: 'region', name: 'East', level: 'ORGLEVEL_2' }]
 */
export function navigateToLevel(currentPath = [], targetIndex) {
  if (!currentPath || !Array.isArray(currentPath)) {
    return [];
  }

  if (targetIndex < 0) {
    return [];
  }

  // Slice up to and including the target index
  return currentPath.slice(0, targetIndex + 1);
}

/**
 * Goes back one level in the hierarchy (toward the root/highest level)
 *
 * @param {DrilldownPathItem[]} currentPath - Current drilldown path
 * @returns {DrilldownPathItem[]} New path with last item removed
 *
 * @example
 * // At Area level, go back to Region
 * const path = [
 *   { id: 'region', name: 'East', level: 'ORGLEVEL_2' },
 *   { id: 'area', name: 'NYC', level: 'ORGLEVEL_1' },
 * ];
 * navigateBack(path);
 * // Returns: [{ id: 'region', name: 'East', level: 'ORGLEVEL_2' }]
 */
export function navigateBack(currentPath = []) {
  if (!currentPath || !Array.isArray(currentPath) || currentPath.length === 0) {
    return [];
  }

  return currentPath.slice(0, -1);
}

/**
 * Resets navigation to the root level (empty path = show all at highest level)
 *
 * @returns {DrilldownPathItem[]} Empty path (root level)
 */
export function navigateToRoot() {
  return [];
}

/**
 * Gets the current drilldown node (last item in path = deepest level)
 *
 * @param {DrilldownPathItem[]} currentPath - Current drilldown path
 * @returns {DrilldownPathItem|null} Current node or null if at root
 *
 * @example
 * const path = [
 *   { id: 'region', name: 'East', level: 'ORGLEVEL_2' },
 *   { id: 'area', name: 'NYC', level: 'ORGLEVEL_1' },
 * ];
 * getCurrentNode(path);
 * // Returns: { id: 'area', name: 'NYC', level: 'ORGLEVEL_1' }
 */
export function getCurrentNode(currentPath = []) {
  if (!currentPath || !Array.isArray(currentPath) || currentPath.length === 0) {
    return null;
  }

  return currentPath[currentPath.length - 1];
}

/**
 * Checks if the user can navigate back (not at root)
 *
 * @param {DrilldownPathItem[]} currentPath - Current drilldown path
 * @returns {boolean} True if can navigate back
 */
export function canNavigateBack(currentPath = []) {
  return Array.isArray(currentPath) && currentPath.length > 0;
}

/**
 * Gets the parent node (second to last in path)
 *
 * @param {DrilldownPathItem[]} currentPath - Current drilldown path
 * @returns {DrilldownPathItem|null} Parent node or null if at root or one level deep
 */
export function getParentNode(currentPath = []) {
  if (!currentPath || !Array.isArray(currentPath) || currentPath.length < 2) {
    return null;
  }

  return currentPath[currentPath.length - 2];
}

/**
 * Creates the initial drilldown path for a root-level view
 * Uses the highest OrgLevel if available, otherwise starts at Location level
 *
 * @param {string} [rootId] - ID for the root node (optional, only needed if showing a specific root)
 * @param {string} [rootName] - Name for the root node
 * @param {Object} [hierarchyMetadata] - Backend metadata
 * @returns {DrilldownPathItem[]} Initial path (empty for root aggregate view)
 *
 * @example
 * // For a dashboard, typically start with empty path (show all at highest level)
 * createInitialPath();
 * // Returns: []
 *
 * @example
 * // If you need to start at a specific root node
 * createInitialPath('region-east', 'East Region', metadata);
 * // Returns: [{ id: 'region-east', name: 'East Region', level: 'ORGLEVEL_2' }]
 */
export function createInitialPath(rootId = null, rootName = null, hierarchyMetadata = null) {
  // For the dashboard, we typically start with an empty path
  // which means "show all data at the highest level"
  if (!rootId) {
    return [];
  }

  // If a specific root is provided, determine its level
  const highestLevel = getHighestLevel(hierarchyMetadata);
  const level = highestLevel?.level || ORG_LEVELS.LOCATION_LEVEL;

  return [
    {
      id: rootId,
      name: rootName || 'Root',
      level,
    },
  ];
}

/**
 * Filters hierarchy data for display based on current navigation state.
 *
 * When at root (empty path): Shows all nodes at the highest OrgLevel
 * When drilled into a node: Shows children of that node (next level down)
 *
 * @param {Array} hierarchyData - Flat array of all hierarchy nodes from backend
 * @param {DrilldownPathItem[]} drilldownPath - Current navigation path
 * @param {Object} [hierarchyMetadata] - Backend metadata with level configuration
 * @returns {Array} Filtered nodes to display at current level
 *
 * @example
 * // At root level - show all ORGLEVEL_2 nodes
 * filterHierarchyForDisplay(allNodes, [], metadata);
 *
 * @example
 * // Drilled into 'region-east' - show its children (ORGLEVEL_1 nodes)
 * filterHierarchyForDisplay(allNodes, [{ id: 'region-east', level: 'ORGLEVEL_2' }], metadata);
 */
export function filterHierarchyForDisplay(hierarchyData = [], drilldownPath = [], hierarchyMetadata = null) {
  if (!hierarchyData || !Array.isArray(hierarchyData) || hierarchyData.length === 0) {
    return [];
  }

  // Get active levels sorted for drilldown (highest first)
  const activeLevels = getActiveLevels(hierarchyMetadata);

  if (activeLevels.length === 0) {
    return hierarchyData;
  }

  // If at root (empty path), show nodes at highest level
  if (!drilldownPath || drilldownPath.length === 0) {
    const highestLevel = activeLevels[0];
    return hierarchyData.filter((node) => node.level === highestLevel.level);
  }

  // If drilled into a node, show its children
  const currentNode = drilldownPath[drilldownPath.length - 1];

  // Find the current level in the active levels
  const currentLevelIndex = activeLevels.findIndex((l) => l.level === currentNode.level);

  if (currentLevelIndex < 0 || currentLevelIndex >= activeLevels.length - 1) {
    // Can't drill down further (at location level or unknown level)
    return [];
  }

  // Get the next level down
  const nextLevel = activeLevels[currentLevelIndex + 1];

  // Filter nodes that:
  // 1. Are at the next level down
  // 2. Have the current node as their parent
  return hierarchyData.filter(
    (node) => node.level === nextLevel.level && node.parentId === currentNode.id
  );
}

/**
 * Gets the level that should be displayed based on current navigation
 *
 * @param {DrilldownPathItem[]} drilldownPath - Current navigation path
 * @param {Object} [hierarchyMetadata] - Backend metadata with level configuration
 * @returns {{ level: string, displayName: string } | null} Level info for display
 */
export function getDisplayLevel(drilldownPath = [], hierarchyMetadata = null) {
  const activeLevels = getActiveLevels(hierarchyMetadata);

  if (activeLevels.length === 0) {
    return null;
  }

  // If at root, display the highest level
  if (!drilldownPath || drilldownPath.length === 0) {
    return activeLevels[0];
  }

  // If drilled in, display the next level down
  const currentNode = drilldownPath[drilldownPath.length - 1];
  const currentLevelIndex = activeLevels.findIndex((l) => l.level === currentNode.level);

  if (currentLevelIndex >= 0 && currentLevelIndex < activeLevels.length - 1) {
    return activeLevels[currentLevelIndex + 1];
  }

  // At deepest level
  return activeLevels[activeLevels.length - 1];
}
